/**
 * One live global-variable cache per preview iframe.
 *
 * Custom Variables and Design are two views of the same engine state. Keeping a
 * separate `get-global-variables` result in each panel made them disagree and
 * doubled an expensive iframe round trip. This store loads once, multicasts
 * runtime pushes, and lets either editor update both views synchronously.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { sendCommand } from "./playEngineBridge";
import { setIn } from "./setIn";

/**
 * How long a local write keeps ownership of its key.
 *
 * Long enough to cover a save and the engine's echo of it; short enough that a
 * write the engine never acknowledged does not freeze the value against real
 * runtime changes for the rest of the session.
 */
const EDITOR_WRITE_WINDOW_MS = 2000;

const entries = new Map();
let isMessageListenerInstalled = false;

function createEntry(iFrameId) {
  return {
    iFrameId,
    versionKey: null,
    variables: {},
    overridesByLanguage: {},
    lastPersistedVariables: {},
    lastPersistedOverrides: {},
    runtimeChangedKeys: new Set(),
    listeners: new Set(),
    loaded: false,
    loading: false,
    loadError: null,
    loadPromise: null,
    generation: 0,
    editorWrites: new Map(),
  };
}

function getEntry(iFrameId) {
  const key = String(iFrameId || "playengine-preview");
  if (!entries.has(key)) entries.set(key, createEntry(iFrameId));
  return entries.get(key);
}

function notify(entry) {
  for (const listener of entry.listeners) listener();
}

function persistedConfig(selectedVersion) {
  const config = _.get(selectedVersion, "playEngineConfig") || {};
  return {
    variables: _.cloneDeep(config.globalVariables) || {},
    overrides: _.cloneDeep(_.get(config, "localization.globalVariableOverrides")) || {},
  };
}

function prepareEntry(entry, selectedVersion) {
  const versionKey = String(_.get(selectedVersion, "_id", ""));
  if (entry.versionKey === versionKey) return;
  const persisted = persistedConfig(selectedVersion);
  entry.versionKey = versionKey;
  entry.variables = persisted.variables;
  entry.overridesByLanguage = persisted.overrides;
  entry.lastPersistedVariables = _.cloneDeep(persisted.variables);
  entry.lastPersistedOverrides = _.cloneDeep(persisted.overrides);
  entry.runtimeChangedKeys = new Set();
  entry.loaded = false;
  entry.loading = false;
  entry.loadError = null;
  entry.loadPromise = null;
  entry.editorWrites = new Map();
  entry.generation++;
}

function changedBranches(previous, next, path = []) {
  const previousTree = _.isPlainObject(previous) || Array.isArray(previous);
  const nextTree = _.isPlainObject(next) || Array.isArray(next);
  if (!previousTree || !nextTree || Array.isArray(previous) !== Array.isArray(next)) {
    return _.isEqual(previous, next)
      ? []
      : [{ path, exists: next !== undefined, value: _.cloneDeep(next) }];
  }

  return _.union(Object.keys(previous), Object.keys(next)).flatMap((key) =>
    changedBranches(previous[key], next[key], [...path, key])
  );
}

/** Merge authored changes without discarding unrelated values changed at runtime. */
export function mergePersistedVariableChanges(liveVariables, previousPersisted, nextPersisted) {
  let merged = _.cloneDeep(liveVariables) || {};
  for (const change of changedBranches(previousPersisted, nextPersisted)) {
    if (change.path.length === 0) {
      merged = change.exists ? _.cloneDeep(change.value) : {};
    } else if (change.exists) {
      _.set(merged, change.path, change.value);
    } else {
      _.unset(merged, change.path);
    }
  }
  return merged;
}

function syncPersistedConfig(entry, selectedVersion) {
  const persisted = persistedConfig(selectedVersion);
  let changed = false;

  if (!_.isEqual(entry.lastPersistedVariables, persisted.variables)) {
    const authoredPaths = changedBranches(
      entry.lastPersistedVariables,
      persisted.variables
    ).map((change) => change.path.join("."));
    entry.variables = mergePersistedVariableChanges(
      entry.variables,
      entry.lastPersistedVariables,
      persisted.variables
    );
    entry.lastPersistedVariables = _.cloneDeep(persisted.variables);
    entry.runtimeChangedKeys = new Set(
      [...entry.runtimeChangedKeys].filter(
        (runtimePath) =>
          !authoredPaths.some(
            (authoredPath) =>
              !authoredPath ||
              runtimePath === authoredPath ||
              runtimePath.startsWith(`${authoredPath}.`) ||
              authoredPath.startsWith(`${runtimePath}.`)
          )
      )
    );
    entry.generation++;
    changed = true;
  }

  if (!_.isEqual(entry.lastPersistedOverrides, persisted.overrides)) {
    entry.overridesByLanguage = persisted.overrides;
    entry.lastPersistedOverrides = _.cloneDeep(persisted.overrides);
    entry.generation++;
    changed = true;
  }

  if (changed) notify(entry);
}

async function loadEntry(entry, isPreviewLoaded, force = false) {
  if (entry.loadPromise) return entry.loadPromise;
  if (entry.loaded && !force) return entry.variables;
  if (!entry.iFrameId) {
    entry.loaded = true;
    notify(entry);
    return entry.variables;
  }
  // The persisted fallback is already available, but it is not considered a
  // completed live load. When the preview becomes ready this same cache will do
  // its single iframe read.
  if (!isPreviewLoaded) return entry.variables;

  const versionKey = entry.versionKey;
  const generation = entry.generation;
  entry.loading = true;
  entry.loadError = null;
  notify(entry);

  entry.loadPromise = (async () => {
    try {
      const response = await sendCommand(
        entry.iFrameId,
        { type: "playengine:get-global-variables" },
        3000
      );
      if (
        entry.versionKey === versionKey &&
        entry.generation === generation &&
        response?.variables &&
        typeof response.variables === "object"
      ) {
        entry.variables = _.cloneDeep(response.variables);
        entry.runtimeChangedKeys = new Set();
      }
      entry.loaded = true;
      return entry.variables;
    } catch (error) {
      if (entry.versionKey === versionKey) {
        // Persisted values remain usable; the error only explains why refresh did
        // not replace them with the live engine state.
        entry.loadError = Object.keys(entry.variables || {}).length
          ? null
          : error?.message || "Could not load custom variables from preview.";
        entry.loaded = true;
      }
      return entry.variables;
    } finally {
      if (entry.versionKey === versionKey) {
        entry.loading = false;
        entry.loadPromise = null;
        notify(entry);
      }
    }
  })();

  return entry.loadPromise;
}

function setVariable(entry, path, value, runtime = false) {
  const segments = Array.isArray(path) ? path : String(path).split(".");
  const dotted = segments.join(".");
  // Only the nodes on this path are rebuilt. Every other variable keeps its
  // identity, so a field elsewhere in the tree is not told it changed — which is
  // what lets the controls hold an edit in progress across a save landing.
  const next = setIn(entry.variables || {}, segments, _.cloneDeep(value));
  entry.variables = next;
  entry.generation++;
  if (runtime) {
    entry.runtimeChangedKeys = new Set(entry.runtimeChangedKeys).add(dotted);
  } else {
    entry.runtimeChangedKeys = new Set(
      [...entry.runtimeChangedKeys].filter(
        (runtimePath) =>
          runtimePath !== dotted &&
          !runtimePath.startsWith(`${dotted}.`) &&
          !dotted.startsWith(`${runtimePath}.`)
      )
    );
    entry.editorWrites.set(dotted, { value: _.cloneDeep(value), isOverride: false, at: Date.now() });
  }
  notify(entry);
  return next;
}

function setOverride(entry, language, path, value) {
  const languageMap = {
    ...(_.get(entry.overridesByLanguage, language) || {}),
    [path]: _.cloneDeep(value),
  };
  entry.overridesByLanguage = { ...entry.overridesByLanguage, [language]: languageMap };
  entry.editorWrites.set(path, { value: _.cloneDeep(value), isOverride: true, at: Date.now() });
  entry.generation++;
  notify(entry);
  return languageMap;
}

function clearOverrides(entry, language, path) {
  const languageMap = { ...(_.get(entry.overridesByLanguage, language) || {}) };
  const removed = Object.keys(languageMap).filter(
    (candidate) => candidate === path || candidate.startsWith(`${path}.`)
  );
  for (const candidate of removed) {
    delete languageMap[candidate];
    // Clearing an override can echo the fallback value as a normal variable
    // change. It is still an override operation and must not touch the base.
    entry.editorWrites.set(candidate, { value: undefined, isOverride: true, at: Date.now() });
  }
  entry.overridesByLanguage = { ...entry.overridesByLanguage, [language]: languageMap };
  entry.generation++;
  notify(entry);
  return { languageMap, removed };
}

/**
 * What to do with a value the engine reports for a key.
 *
 * A KEY BEING EDITED IS NEVER OVERWRITTEN FROM OUTSIDE. The engine echoes every
 * value it is handed, so a burst of keystrokes produces a trail of echoes
 * carrying the values sent along the way. The old rule applied any echo whose
 * value did not match the latest local one — which is exactly the stale ones —
 * so typing "hello" quickly could be yanked back to "he" mid-word. An override
 * echo is worse still: the engine reports the effective value for the current
 * language, and copying that onto the base would overwrite English with a
 * translation.
 *
 * The echo carrying the value last written locally means the engine has caught
 * up, and settles the write. Anything else arriving while a write is
 * outstanding is stale and is dropped. With nothing outstanding the engine is
 * the authority — gameplay, a script or a timeline moved the value.
 *
 * @param {{ value: *, isOverride: boolean, at: number }|undefined} pending
 * @param {*} echoValue
 * @param {number} now
 * @returns {"apply"|"ignore"|"settle"}
 */
export function resolveEcho(pending, echoValue, now) {
  if (!pending) return "apply";
  if (now - pending.at >= EDITOR_WRITE_WINDOW_MS) return "apply";
  if (!pending.isOverride && _.isEqual(pending.value, echoValue)) return "settle";
  return "ignore";
}

function installMessageListener() {
  if (isMessageListenerInstalled || typeof window === "undefined") return;
  isMessageListenerInstalled = true;
  window.addEventListener("message", (event) => {
    if (event.data?.type !== "playengine:global-variable-changed") return;
    for (const entry of entries.values()) {
      const iframe = document.getElementById(entry.iFrameId);
      if (!iframe || event.source !== iframe.contentWindow) continue;
      const pending = entry.editorWrites.get(event.data.key);
      const verdict = resolveEcho(pending, event.data.value, Date.now());

      if (verdict !== "apply") {
        if (verdict === "settle") entry.editorWrites.delete(event.data.key);
        continue;
      }
      if (pending) entry.editorWrites.delete(event.data.key);
      setVariable(entry, event.data.key, event.data.value, true);
    }
  });
}

/** Shared React view of the cache used by both variable editors. */
export function usePlayEngineGlobalVariables({
  iFrameId,
  isPreviewLoaded,
  selectedVersion,
}) {
  const entry = useMemo(() => getEntry(iFrameId), [iFrameId]);
  prepareEntry(entry, selectedVersion);
  const [, setRevision] = useState(0);

  useEffect(() => {
    const rerender = () => setRevision((revision) => revision + 1);
    entry.listeners.add(rerender);
    installMessageListener();
    return () => entry.listeners.delete(rerender);
  }, [entry]);

  useEffect(() => {
    syncPersistedConfig(entry, selectedVersion);
  }, [entry, selectedVersion]);

  useEffect(() => {
    if (!isPreviewLoaded) {
      // The same version can receive a brand-new iframe after Apply Preview.
      // Keep serving the cache while it is down, but make the next ready edge do
      // one fresh load for both subscribers.
      entry.loaded = false;
      return;
    }
    void loadEntry(entry, true);
  }, [entry, entry.versionKey, isPreviewLoaded]);

  return {
    variables: entry.variables,
    overridesByLanguage: entry.overridesByLanguage,
    runtimeChangedKeys: entry.runtimeChangedKeys,
    loading: entry.loading,
    loadError: entry.loadError,
    reload: useCallback(() => loadEntry(entry, isPreviewLoaded, true), [entry, isPreviewLoaded]),
    setVariable: useCallback((path, value) => setVariable(entry, path, value), [entry]),
    setOverride: useCallback(
      (language, path, value) => setOverride(entry, language, path, value),
      [entry]
    ),
    clearOverrides: useCallback(
      (language, path) => clearOverrides(entry, language, path),
      [entry]
    ),
  };
}
